import { useTranslation } from "react-i18next"

import { DashboardKpiTile } from "@/components/brand/dashboard/dashboard-kpi-tile"
import type {
  CrewAnalyticsKpisSnapshot,
  CrewAnalyticsKpisWindow,
  CrewAnalyticsWindow,
} from "@/types/crew-analytics"

/**
 * OKS-65 — 4-tile cohort KPI strip for `/brand/analytics/crew`.
 *
 * Two prop Hashes drive the strip per the TRD K8 split-KPI lock:
 *   - `snapshot` = window-INVARIANT (total_active, pct_oauth, pct_at_risk)
 *   - `window`   = window-SCOPED (median_reliability)
 *
 * Reuses the existing OKS-38 `<DashboardKpiTile>` primitive (already
 * supports `integer` and `percent` kinds; `null` percent renders the
 * em-dash with the empty-aria fallback).
 */
interface CrewAnalyticsKpiStripProps {
  snapshot: CrewAnalyticsKpisSnapshot
  window: CrewAnalyticsKpisWindow
  /** Echoed for caption interpolation (the "in last {window}" line). */
  windowLabel: CrewAnalyticsWindow
}

export function CrewAnalyticsKpiStrip({
  snapshot,
  window,
  windowLabel,
}: CrewAnalyticsKpiStripProps) {
  const { t } = useTranslation()

  const windowLabelText = t(`analytics.crew.window.${windowLabel}`)

  return (
    <section
      className="grid grid-cols-2 gap-4 lg:grid-cols-4"
      aria-label={t("analytics.crew.kpis.section_aria")}
      data-testid="crew-analytics-kpi-strip"
    >
      <DashboardKpiTile
        labelKey="analytics.crew.kpis.total_active.label"
        ariaLabelKey="analytics.crew.kpis.total_active.aria"
        value={snapshot.total_active}
        kind="integer"
        testId="crew-kpi-total-active"
      />
      <DashboardKpiTile
        labelKey="analytics.crew.kpis.pct_oauth.label"
        ariaLabelKey="analytics.crew.kpis.pct_oauth.aria"
        emptyAriaKey="analytics.crew.kpis.pct_oauth.empty_aria"
        value={snapshot.pct_oauth}
        kind="percent"
        testId="crew-kpi-pct-oauth"
      />
      <DashboardKpiTile
        labelKey="analytics.crew.kpis.median_reliability.label"
        ariaLabelKey="analytics.crew.kpis.median_reliability.aria"
        emptyAriaKey="analytics.crew.kpis.median_reliability.empty_aria"
        value={window.median_reliability}
        kind="integer"
        testId="crew-kpi-median-reliability"
      />
      <DashboardKpiTile
        labelKey="analytics.crew.kpis.pct_at_risk.label"
        ariaLabelKey="analytics.crew.kpis.pct_at_risk.aria"
        emptyAriaKey="analytics.crew.kpis.pct_at_risk.empty_aria"
        value={snapshot.pct_at_risk}
        kind="percent"
        testId="crew-kpi-pct-at-risk"
      />
      <p className="text-muted-foreground col-span-2 -mt-2 text-xs lg:col-span-4">
        {t("analytics.crew.kpis.caption", { window: windowLabelText })}
      </p>
    </section>
  )
}
